import { createTheme, responsiveFontSizes } from "@mui/material/styles";

const FONT_DISPLAY = `"Fraunces", "Georgia", serif`;
const FONT_BODY = `"Outfit", "Segoe UI", sans-serif`;

/**
 * Cocktail-bar theme: deep navy / charcoal with amber-gold accents.
 * Supports light and dark via ColorModeContext.
 */
export const createAppTheme = (mode, isMobile) => {
    const isLight = mode === "light";

    let baseTheme = createTheme({
        palette: {
            mode,
            primary: {
                main: isLight ? "#1B2B44" : "#D4A84B",
                light: isLight ? "#2F4568" : "#E0C06E",
                dark: isLight ? "#121C2E" : "#B8923A",
                contrastText: isLight ? "#F7F5F0" : "#1A1610",
            },
            secondary: {
                main: isLight ? "#B8860B" : "#E8C547",
                light: isLight ? "#D4A84B" : "#F0D97A",
                dark: isLight ? "#8B6914" : "#C9A227",
                contrastText: isLight ? "#1A1610" : "#1A1610",
            },
            text: {
                primary: isLight ? "#1A222D" : "#F0EDE6",
                secondary: isLight ? "rgba(26, 34, 45, 0.68)" : "rgba(240, 237, 230, 0.72)",
                disabled: isLight ? "rgba(26, 34, 45, 0.45)" : "rgba(240, 237, 230, 0.45)",
            },
            divider: isLight ? "rgba(27, 43, 68, 0.12)" : "rgba(212, 168, 75, 0.16)",
            background: {
                default: isLight ? "#F5F6F8" : "#0F1419",
                paper: isLight ? "#FFFFFF" : "#1A222D",
                primary: isLight ? "#FFFFFF" : "#121820",
                secondary: isLight ? "#EEF0F3" : "#1A222D",

                red: isLight ? "#fce4ec" : "#ff72a1",
                "red-a": isLight ? "#fce4ec" : "#ff72a1",
                "red-b": isLight ? "#fad4e0" : "#f15083",

                lightblue: isLight ? "#d6e4f0" : "#8eb4d4",
                "lightblue-a": isLight ? "#d6e4f0" : "#7aa3c7",
                "lightblue-b": isLight ? "#c2d6e8" : "#5f8fb8",

                blue: isLight ? "#b8c9dc" : "#5a7fa3",
                "blue-a": isLight ? "#b8c9dc" : "#6a8fb0",
                "blue-b": isLight ? "#a3b8cf" : "#4a6f93",

                gray: isLight ? "#f0f1f3" : "#b3b3b3",
                "gray-a": isLight ? "#f0f1f3" : "#dadada",
                "gray-b": isLight ? "#e2e4e8" : "#b4b4b4",

                orange: isLight ? "#f7edd0" : "#ddd168",
                "orange-a": isLight ? "#f7edd0" : "#e7de88",
                "orange-b": isLight ? "#efe0b0" : "#c2b759",

                purple: isLight ? "#e8dce8" : "#a87aa8",
                "purple-a": isLight ? "#e8dce8" : "#b890b8",
                "purple-b": isLight ? "#dccedc" : "#906890",

                green: isLight ? "#d4e4c8" : "#96c465",
                "green-a": isLight ? "#d4e4c8" : "#a1ca75",
                "green-b": isLight ? "#c5d9b5" : "#82b352",

                button: {
                    red: isLight ? "#c0392b" : "#e45c5c",
                    blue: isLight ? "#1B2B44" : "#6a8fb0",
                    gray: isLight ? "#5c6570" : "#a3a3a3",
                    orange: isLight ? "#B8860B" : "#E8C547",
                    purple: isLight ? "#6B3A5B" : "#b07a9a",
                    green: isLight ? "#4a7c3f" : "#95cf62",
                },
            },
        },
        typography: {
            fontFamily: FONT_BODY,
            fontSize: isMobile ? 12 : 16,
            h1: { fontFamily: FONT_DISPLAY, fontWeight: 600, letterSpacing: "-0.02em" },
            h2: { fontFamily: FONT_DISPLAY, fontWeight: 600, letterSpacing: "-0.02em" },
            h3: { fontFamily: FONT_DISPLAY, fontWeight: 600, letterSpacing: "-0.015em" },
            h4: { fontFamily: FONT_DISPLAY, fontWeight: 600, letterSpacing: "-0.01em" },
            h5: { fontFamily: FONT_DISPLAY, fontWeight: 550, letterSpacing: "-0.01em" },
            h6: { fontFamily: FONT_DISPLAY, fontWeight: 550, letterSpacing: "-0.005em" },
            subtitle1: { fontFamily: FONT_BODY, fontWeight: 500 },
            subtitle2: { fontFamily: FONT_BODY, fontWeight: 500 },
            button: { fontFamily: FONT_BODY, fontWeight: 600, letterSpacing: "0.02em", textTransform: "none" },
        },
        tablerow: {
            fontSize: isMobile ? 8 : 14,
        },
        input: {
            fontSize: isMobile ? 8 : 14,
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiListItemIcon: {
                styleOverrides: {
                    root: {
                        minWidth: 30,
                    },
                },
            },
            MuiListItemText: {
                styleOverrides: {
                    root: {
                        marginTop: 1,
                        marginBottom: 1,
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        padding: "5px 2px",
                    },
                },
            },
        },
    });

    // After creating the baseTheme, we can use variables like palette.primary.main.
    const dynamicTheme = createTheme(baseTheme, {
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: baseTheme.palette.background.default,
                        color: baseTheme.palette.text.primary,
                    },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: {
                        backgroundColor: baseTheme.palette.primary.main,
                        color: baseTheme.palette.primary.contrastText,
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: isLight
                            ? baseTheme.palette.primary.main
                            : baseTheme.palette.background.paper,
                        color: isLight
                            ? baseTheme.palette.primary.contrastText
                            : baseTheme.palette.text.primary,
                        borderBottom: isLight
                            ? "none"
                            : `1px solid ${baseTheme.palette.divider}`,
                        backgroundImage: "none",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: baseTheme.palette.background.paper,
                        backgroundImage: "none",
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                    },
                    containedPrimary: {
                        boxShadow: isLight
                            ? "0 1px 3px rgba(27, 43, 68, 0.25)"
                            : "0 1px 3px rgba(0, 0, 0, 0.4)",
                    },
                },
            },
            MuiFab: {
                styleOverrides: {
                    primary: {
                        backgroundColor: baseTheme.palette.secondary.main,
                        color: baseTheme.palette.secondary.contrastText,
                        "&:hover": {
                            backgroundColor: baseTheme.palette.secondary.dark,
                        },
                    },
                },
            },
        },
    });

    return responsiveFontSizes(dynamicTheme);
};
